import { useCallback, useEffect, useRef, useState } from 'react';

import VoiceConversationController, { INITIAL_VOICE_STATE } from './VoiceConversationController';

/**
 * Function-component wrapper around VoiceConversationController (ATAI-231).
 * No voice logic lives here: one controller per mount, its state mirrored into
 * React state, destroyed on unmount.
 *
 * `onSubmit` / `onTranscript` may change between renders; the latest ones are
 * always called.
 *
 * Returns the voice state keys (see INITIAL_VOICE_STATE) plus the actions.
 */
export default function useVoiceConversation({ onSubmit, onTranscript, stripMarkup = false } = {}) {
  const callbacksRef = useRef({ onSubmit, onTranscript });
  callbacksRef.current = { onSubmit, onTranscript };

  const [voiceState, setVoiceState] = useState(INITIAL_VOICE_STATE);
  const controllerRef = useRef(null);

  const createController = () =>
    new VoiceConversationController({
      stripMarkup,
      onStateChange: (partial) => setVoiceState((prev) => ({ ...prev, ...partial })),
      onSubmit: (text) => callbacksRef.current.onSubmit?.(text),
      onTranscript: (text) => callbacksRef.current.onTranscript?.(text),
    });

  if (!controllerRef.current) controllerRef.current = createController();

  useEffect(() => {
    // A remount after cleanup (e.g. StrictMode's double effect) needs a live
    // controller again; a destroyed one ignores every call.
    if (controllerRef.current.destroyed) {
      controllerRef.current = createController();
      setVoiceState(INITIAL_VOICE_STATE);
    }
    const controller = controllerRef.current;
    controller.detectSupport();
    return () => controller.destroy();
  }, []);

  const start = useCallback(() => controllerRef.current.start(), []);
  const toggleListening = useCallback(() => controllerRef.current.toggleListening(), []);
  const end = useCallback(() => controllerRef.current.end(), []);
  const reset = useCallback(() => controllerRef.current.reset(), []);
  const toggleMicMute = useCallback(() => controllerRef.current.toggleMicMute(), []);
  const toggleOutputMute = useCallback(() => controllerRef.current.toggleOutputMute(), []);
  const speak = useCallback((text) => controllerRef.current.speak(text), []);
  const cancelSpeech = useCallback(() => controllerRef.current.cancelSpeech(), []);
  const setBusy = useCallback((busy) => controllerRef.current.setBusy(busy), []);
  const restartIfReady = useCallback(() => controllerRef.current.restartIfReady(), []);
  const clearTranscript = useCallback(() => controllerRef.current.clearTranscript(), []);
  const retryMicPermission = useCallback(() => controllerRef.current.retryMicPermission(), []);
  const dismissMicPermission = useCallback(
    () => controllerRef.current.dismissMicPermission(),
    []
  );
  const retryNetworkError = useCallback(() => controllerRef.current.retryNetworkError(), []);
  const dismissNetworkError = useCallback(() => controllerRef.current.dismissNetworkError(), []);

  return {
    ...voiceState,
    start,
    toggleListening,
    end,
    reset,
    toggleMicMute,
    toggleOutputMute,
    speak,
    cancelSpeech,
    setBusy,
    restartIfReady,
    clearTranscript,
    retryMicPermission,
    dismissMicPermission,
    retryNetworkError,
    dismissNetworkError,
  };
}
